import { useMutation, useQuery } from "@apollo/client/react";
import { OrganizationApp } from "../../../../../gql/graphql";
import { useNavigate, useParams } from "react-router-dom";
import { CreationForm } from "../../../components/form/CreationForm";
import * as yup from "yup";
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  List,
  Skeleton,
  Stack,
  Tabs,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowLeft,
  IconCopy,
  IconHistory,
  IconInfoCircle,
  IconKey,
  IconPencil,
  IconWebhook,
} from "@tabler/icons-react";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { ManifestSummary } from "../../developer/app-detail/ManifestSummary";
import {
  formatDate,
  RELEASE_STATUS_COLORS,
} from "../../developer/app-detail/types";
import {
  ORG_APP_DETAIL_QUERY,
  UPDATE_APP_MUTATION,
  URL_REGEX,
} from "./queries";
import { CreateAppForm } from "./CreateAppForm";

export function EditApp() {
  const navigate = useNavigate();
  const appId = useParams().appId;

  const [updateApp] = useMutation(UPDATE_APP_MUTATION);

  const { data, loading, refetch } = useQuery<{
    organizationApp: OrganizationApp;
  }>(ORG_APP_DETAIL_QUERY, {
    variables: { id: appId },
    skip: !appId,
  });

  // Create mode — just the form
  if (!appId) {
    return <CreateAppForm />;
  }

  if (loading) {
    return (
      <Stack gap="lg">
        <Skeleton height={60} />
        <Skeleton height={80} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  const app = data?.organizationApp;
  if (!app) {
    return <Text c="red">App not found</Text>;
  }

  const config = app.configuration;
  const webhooks = app.appWebhooks ?? [];
  const releases = app.releases ?? [];
  const manifest = app.manifest;
  const creator = app.creator;

  const openEdit = () => {
    const modalId = `update-app-${appId}`;
    modals.open({
      modalId,
      title: "Update App",
      size: "lg",
      children: (
        <CreationForm
          schemaDefinition={{
            name: {
              type: "INPUT",
              inputType: "text",
              label: "Name",
              yupConfig: yup.string().required(),
              defaultValue: app.name,
              placeholder: "My app",
            },
            description: {
              type: "TEXTAREA",
              label: "Description",
              defaultValue: app.description,
              yupConfig: yup.string().required(),
            },
            loginUrl: {
              type: "INPUT",
              inputType: "text",
              label: "Login URL",
              defaultValue: config.metadata.loginUrl,
              yupConfig: yup
                .string()
                .matches(URL_REGEX)
                .required()
                .typeError("Must be a valid URL"),
              placeholder: "https://myapp.com/login",
            },
            consentUrl: {
              type: "INPUT",
              inputType: "text",
              label: "Consent URL",
              defaultValue: config.metadata.consentUrl,
              yupConfig: yup
                .string()
                .matches(URL_REGEX)
                .required()
                .typeError("Must be a valid URL"),
              placeholder: "https://myapp.com/consent",
            },
            logoutRedirectUrls: {
              type: "INPUT",
              inputType: "text",
              label: "Logout redirect URL",
              defaultValue: config.logoutRedirectUrls[0],
              yupConfig: yup
                .string()
                .matches(URL_REGEX)
                .required()
                .typeError("Must be a valid URL"),
              placeholder: "https://myapp.com/logout",
            },
            redirectUrls: {
              type: "INPUT",
              inputType: "text",
              label: "Redirect URL",
              subtext:
                "URL where users will be redirected after successful authentication. It is important to note that the redirect URL is called ONLY if there are more than 2 identity providers set as 'login allowed' to true, because we are not able to know which one to use for the login process, if only one is available, this URL redirect will be skipped. If you have more than 1 identity provider set to 'allowed login', you will need to manage the login page yourself to allow for the identity provider selection",
              defaultValue: config.redirectUrls[0],
              yupConfig: yup
                .string()
                .matches(URL_REGEX)
                .required()
                .typeError("Must be a valid URL"),
              placeholder: "https://myapp.com/redirect",
            },
            requiresConsent: {
              type: "CHECKBOX",
              label: "Requires consent",
              defaultValue: config.metadata.requiresConsent ?? false,
              yupConfig: yup.boolean().default(false),
            },
          }}
          onSubmit={async (formData) => {
            try {
              await updateApp({
                variables: {
                  id: appId,
                  name: formData.name,
                  description: formData.description,
                  loginUrl: formData.loginUrl,
                  consentUrl: formData.consentUrl,
                  logoutRedirectUrls: [formData.logoutRedirectUrls],
                  redirectUrls: [formData.redirectUrls],
                  requiresConsent: formData.requiresConsent,
                },
              }).then((res) => {
                if (res?.error) {
                  throw new Error(res.error.message);
                }
              });

              notifications.show({
                title: "Updated",
                message: "App updated successfully.",
                color: "green",
                autoClose: 3000,
              });

              modals.close(modalId);
              refetch();
            } catch (error) {
              notifications.show({
                title: "Error",
                message:
                  error instanceof Error
                    ? error.message
                    : "Failed to update app",
                color: "red",
              });
            }
          }}
        />
      ),
    });
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="md">
            <ActionIcon
              variant="subtle"
              onClick={() => navigate("/developer/apps")}
              size="lg"
            >
              <IconArrowLeft size={20} />
            </ActionIcon>
            {app.icon && <Avatar src={app.icon} size={56} radius="md" />}
            <Stack gap={2}>
              <Group gap="sm">
                <Title order={3}>{app.name}</Title>
                <Badge color={app.public ? "blue" : "orange"} variant="light">
                  {app.public ? "Public" : "Confidential"}
                </Badge>
                {manifest && (
                  <Badge color="green" variant="light" size="sm">
                    v{manifest.version}
                  </Badge>
                )}
              </Group>
              <Text size="sm" c="dimmed" lineClamp={1}>
                {app.description}
              </Text>
            </Stack>
          </Group>
          <Button
            variant="light"
            leftSection={<IconPencil size={14} />}
            onClick={openEdit}
          >
            Edit App
          </Button>
        </Group>

        {/* IDs Row */}
        <Group gap="lg" ml={52}>
          <Group gap={4}>
            <Text size="xs" c="dimmed">
              Client ID:
            </Text>
            <Code style={{ fontSize: 11 }}>{app.id}</Code>
            <CopyButton value={app.id}>
              {({ copy, copied }) => (
                <Tooltip label={copied ? "Copied" : "Copy"} withArrow>
                  <ActionIcon size="xs" variant="subtle" onClick={copy}>
                    <IconCopy size={12} />
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
          <Group gap={4}>
            <Text size="xs" c="dimmed">
              Creator:
            </Text>
            <Text size="xs">
              {creator?.profiles?.[0]?.username ??
                creator?.email ??
                creator?.id}
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Created: {formatDate(app.createdAt)}
          </Text>
        </Group>
      </Stack>
      {/* Quick Info Cards */}
      <Group gap="md" grow>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">
            Webhooks
          </Text>
          <Group gap={4} mt={2}>
            <Text size="sm" fw={600}>
              {webhooks.length}
            </Text>
            {webhooks.length > 0 && (
              <Badge size="xs" color="green" variant="light">
                {webhooks.filter((w: { active: boolean }) => w.active).length}{" "}
                active
              </Badge>
            )}
          </Group>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">
            Releases
          </Text>
          <Text size="sm" fw={600} mt={2}>
            {releases.length}
          </Text>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">
            Client Type
          </Text>
          <Badge
            color={app.public ? "blue" : "orange"}
            variant="light"
            size="sm"
            mt={2}
          >
            {app.public ? "Public" : "Confidential"}
          </Badge>
        </Card>
        <Card withBorder padding="sm">
          <Text size="xs" c="dimmed">
            Last Updated
          </Text>
          <Text size="sm" mt={2}>
            {formatDate(app.updatedAt)}
          </Text>
        </Card>
      </Group>
      {/* Tabs */}
      <Tabs defaultValue="overview">
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconInfoCircle size={14} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="releases" leftSection={<IconHistory size={14} />}>
            Releases ({releases.length})
          </Tabs.Tab>
          <Tabs.Tab value="credentials" leftSection={<IconKey size={14} />}>
            Credentials
          </Tabs.Tab>
          <Tabs.Tab value="webhooks" leftSection={<IconWebhook size={14} />}>
            Webhooks ({webhooks.length})
          </Tabs.Tab>
        </Tabs.List>

        {/* Overview */}
        <Tabs.Panel value="overview" pt="md">
          <Stack gap="lg">
            {app.description && (
              <Stack gap="xs">
                <Title order={5}>Description</Title>
                <Text size="sm">{app.description}</Text>
              </Stack>
            )}

            {app.shortDescription && (
              <Stack gap="xs">
                <Title order={5}>Short Description</Title>
                <Text size="sm" c="dimmed">
                  {app.shortDescription}
                </Text>
              </Stack>
            )}

            <Group gap="xl">
              <Text size="xs" c="dimmed">
                Created: {formatDate(app.createdAt)}
              </Text>
              <Text size="xs" c="dimmed">
                Updated: {formatDate(app.updatedAt)}
              </Text>
            </Group>

            {manifest && (
              <>
                <Title order={5}>Manifest (v{manifest.version})</Title>
                <Text size="sm" c="dimmed">
                  The manifest defines your app's capabilities, permissions, and
                  registered extension points.
                </Text>
                <ManifestSummary manifest={manifest} />
              </>
            )}
          </Stack>
        </Tabs.Panel>

        {/* Releases */}
        <Tabs.Panel value="releases" pt="md">
          <Stack gap="md">
            <Title order={5}>Releases</Title>
            <Text size="sm" c="dimmed">
              Release history for this app.
            </Text>

            {releases.length === 0 && (
              <Card withBorder padding="xl">
                <Text c="dimmed" ta="center" py="md">
                  No releases yet.
                </Text>
              </Card>
            )}

            {releases.map((release) => (
              <Card key={release.id} withBorder>
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Group gap="sm">
                      <Text fw={600}>v{release.version}</Text>
                      <Badge
                        color={RELEASE_STATUS_COLORS[release.status] ?? "gray"}
                        variant="light"
                      >
                        {release.status.replace(/_/g, " ")}
                      </Badge>
                    </Group>
                    <Text size="xs" c="dimmed">
                      {formatDate(release.createdAt)}
                    </Text>
                  </Group>
                  {release.notes && (
                    <Text size="sm" c="dimmed">
                      {release.notes}
                    </Text>
                  )}
                  {release.reviewResult && (
                    <Card withBorder padding="xs">
                      <Group gap="xs">
                        <Badge
                          size="xs"
                          color={release.reviewResult.passed ? "green" : "red"}
                          variant="light"
                        >
                          Review{" "}
                          {release.reviewResult.passed ? "Passed" : "Failed"}
                        </Badge>
                        {!release.reviewResult.passed && (
                          <List size="xs" spacing={2}>
                            {release.reviewResult.checks
                              .filter((c) => !c.passed)
                              .map((check) => (
                                <List.Item key={check.name}>
                                  <Text size="xs" c="red">
                                    {check.name}
                                    {check.details ? `: ${check.details}` : ""}
                                  </Text>
                                </List.Item>
                              ))}
                          </List>
                        )}
                      </Group>
                    </Card>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>

        {/* Credentials */}
        <Tabs.Panel value="credentials" pt="md">
          <Stack gap="lg">
            <Title order={5}>API Credentials</Title>

            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Client ID
                </Text>
                <Group gap="xs">
                  <Code style={{ flex: 1 }}>{app.id}</Code>
                  <CopyButton value={app.id}>
                    {({ copy, copied }) => (
                      <Button size="xs" variant="subtle" onClick={copy}>
                        {copied ? "Copied" : "Copy"}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              </Stack>
            </Card>

            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Client Type
                </Text>
                <Badge
                  color={app.public ? "blue" : "orange"}
                  variant="light"
                  size="lg"
                >
                  {app.public ? "Public" : "Confidential"}
                </Badge>
                <Text size="xs" c="dimmed">
                  {app.public
                    ? "Public clients do not use a client secret (e.g. SPAs, mobile apps). Uses Authorization Code + PKCE flow."
                    : "Confidential clients use a client secret for server-to-server auth. Uses Client Credentials flow."}
                </Text>
              </Stack>
            </Card>

            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  OAuth2 Scopes
                </Text>
                {config.scope ? (
                  <Group gap={6}>
                    {config.scope
                      .split(" ")
                      .filter(Boolean)
                      .map((s: string) => (
                        <Badge key={s} variant="outline" size="sm">
                          {s}
                        </Badge>
                      ))}
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">
                    No scopes configured
                  </Text>
                )}
              </Stack>
            </Card>

            <Title order={5}>Redirect URLs</Title>

            <Card withBorder>
              <Stack gap="xs">
                <Text size="sm" fw={500}>
                  Redirect URLs
                </Text>
                {config.redirectUrls.length > 0 ? (
                  <List size="sm" spacing={4}>
                    {config.redirectUrls.map((url: string) => (
                      <List.Item key={url}>
                        <Code>{url}</Code>
                      </List.Item>
                    ))}
                  </List>
                ) : (
                  <Text size="sm" c="dimmed">
                    No redirect URLs
                  </Text>
                )}
              </Stack>
            </Card>

            {config.logoutRedirectUrls.length > 0 && (
              <Card withBorder>
                <Stack gap="xs">
                  <Text size="sm" fw={500}>
                    Logout Redirect URLs
                  </Text>
                  <List size="sm" spacing={4}>
                    {config.logoutRedirectUrls.map((url: string) => (
                      <List.Item key={url}>
                        <Code>{url}</Code>
                      </List.Item>
                    ))}
                  </List>
                </Stack>
              </Card>
            )}

            <Title order={5}>Auth Flow</Title>

            <Card withBorder>
              <Group gap="xl">
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Login URL
                  </Text>
                  <Code>{config.metadata.loginUrl || "\u2014"}</Code>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Consent URL
                  </Text>
                  <Code>{config.metadata.consentUrl || "\u2014"}</Code>
                </Stack>
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Requires Consent
                  </Text>
                  <Badge
                    variant="outline"
                    color={config.metadata.requiresConsent ? "yellow" : "green"}
                    size="sm"
                  >
                    {config.metadata.requiresConsent ? "Yes" : "No"}
                  </Badge>
                </Stack>
              </Group>
            </Card>

            <Card withBorder>
              <Group gap="md">
                <Text size="xs" c="dimmed">
                  Created by{" "}
                  {creator?.profiles?.[0]?.username ??
                    creator?.email ??
                    creator?.id}{" "}
                  on {formatDate(app.createdAt)}
                </Text>
              </Group>
            </Card>
          </Stack>
        </Tabs.Panel>

        {/* Webhooks */}
        <Tabs.Panel value="webhooks" pt="md">
          <Stack gap="lg">
            <Title order={5}>Webhooks</Title>
            <Text size="sm" c="dimmed">
              Webhooks receive real-time event notifications when actions occur
              in organizations that have your app installed.
            </Text>

            {webhooks.length === 0 && (
              <Card withBorder padding="xl">
                <Text c="dimmed" ta="center" py="md">
                  No webhooks configured. Webhooks are created when your app is
                  installed and your manifest declares webhookEvents.
                </Text>
              </Card>
            )}

            {webhooks.map((wh) => (
              <Card key={wh.id} withBorder>
                <Stack gap="sm">
                  <Group justify="space-between">
                    <Group gap="xs">
                      <Badge
                        color={wh.active ? "green" : "red"}
                        variant="light"
                        size="sm"
                      >
                        {wh.active ? "Active" : "Inactive"}
                      </Badge>
                      <Code>{wh.url}</Code>
                    </Group>
                    <Text size="xs" c="dimmed">
                      Created {formatDate(wh.createdAt)}
                    </Text>
                  </Group>
                  <Stack gap={4}>
                    <Text size="xs" fw={500}>
                      Events ({wh.events.length})
                    </Text>
                    <Group gap={6}>
                      {wh.events.map((evt: string) => (
                        <Badge
                          key={evt}
                          variant="outline"
                          size="xs"
                          color="violet"
                        >
                          {evt}
                        </Badge>
                      ))}
                    </Group>
                  </Stack>
                  {wh.installationId && (
                    <Group gap={4}>
                      <Text size="xs" c="dimmed">
                        Installation:
                      </Text>
                      <Code style={{ fontSize: 11 }}>{wh.installationId}</Code>
                    </Group>
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
